import React, { useContext, useEffect, useState } from 'react'
import { IUserSession } from 'types'
import api from 'helpers/api'
import { capitaliseFirstLetter } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'
import DataTable, { dataTableColumns } from 'components/data-table'
import DateCell from 'components/data-table/dateCell'
import RowActions from 'components/data-table/rowActions'
import ActionDialog from 'components/dialog/action'
import { LogoutIcon } from 'components/icons'
import { SettingsCard, TextLink } from 'views/settings/components/settingsCard'

// Wide enough for the longest day, so the columns hold still as rows come and go.
const DATE_COLUMN = 'w-[124px]'
// The audit log's resource links: the text's own colour, underlined on hover.
const LINK = 'font-medium! text-body hover:underline!'

/** What a session is on, as the sessions list shows it: "Desktop · macOS". */
export const deviceName = (session: IUserSession) =>
  [
    session.deviceType ? capitaliseFirstLetter(session.deviceType) : 'Unknown device',
    session.platform || 'Unknown platform'
  ].join(' · ')

/**
 * The server stores session times in UTC, usually without saying so; marked as UTC, they show in local time.
 * A time that already names its zone ("Z" or "+00:00") is left as it is.
 */
const utc = (time: string) => (/(Z|[+-]\d{2}:?\d{2})$/i.test(time) ? time : `${time}Z`)

/** What a row's cells need from the card: asking to sign a session out. */
const SessionRows = React.createContext<{ askSignOut: (session: IUserSession) => void }>({
  askSignOut: () => undefined
})

// Behind ⋯, as signing out is destructive: it ends that device's session.
const ActionsCell: React.FC<{ session: IUserSession }> = ({ session }) => {
  const { askSignOut } = useContext(SessionRows)
  return (
    <RowActions
      name={deviceName(session)}
      menu
      actions={[{ label: 'Sign Out', icon: <LogoutIcon />, destructive: true, onSelect: () => askSignOut(session) }]}
    />
  )
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
const col = dataTableColumns<IUserSession>()
const COLUMNS = col.columns([
  col.accessor((session) => deviceName(session), {
    id: 'device',
    header: 'Device',
    cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>
  }),
  col.accessor((session) => utc(session.createdAt), {
    id: 'signedIn',
    header: 'Signed in',
    sortFn: 'datetime',
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.accessor((session) => utc(session.expiresAt), {
    id: 'expires',
    header: 'Expires',
    sortFn: 'datetime',
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.accessor('ipAddress', {
    header: 'IP address',
    cell: ({ getValue }) => {
      const ip = getValue()
      if (!ip) return <span className="text-muted">Unknown</span>
      return (
        <a
          href={`https://ipinfo.io/${ip}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`tabular-nums ${LINK}`}
        >
          {ip}
        </a>
      )
    }
  }),
  col.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'w-0 text-right' },
    cell: ({ row }) => <ActionsCell session={row.original} />
  })
])

/**
 * The devices signed in to the account, newest first, each with Sign Out in its ⋯ menu, which asks first. A
 * failed load says so, with Try again.
 */
const DevicesCard: React.FC = () => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [sessions, setSessions] = useState<IUserSession[]>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [signingOut, setSigningOut] = useState<IUserSession>()

  const load = () => {
    setSessions(undefined)
    setLoadFailed(false)
    api
      .getUserSessions()
      .then((response) => setSessions(response.data))
      .catch(() => setLoadFailed(true))
  }

  useEffect(() => {
    load()
  }, [])

  // Rejects when it fails, having said why, so the confirmation stays open to try again.
  const signOut = async (session: IUserSession) => {
    try {
      await api.deleteUserSession(session.publicId)
    } catch (error) {
      displayErrorMessage(error)
      throw error
    }
    setSessions((current) => current?.filter((each) => each.publicId !== session.publicId))
    setSnackbarMessage({ status: 'success', message: `${deviceName(session)} was signed out` })
  }

  return (
    <SettingsCard
      title="Signed-in devices"
      description="Where this account is signed in. A new sign-in signs out every other device."
    >
      <SessionRows.Provider value={{ askSignOut: setSigningOut }}>
        <DataTable
          label="Signed-in devices"
          rowLabel={deviceName}
          columns={COLUMNS}
          data={sessions}
          getRowId={(session) => session.publicId}
          initialSort={[{ id: 'signedIn', desc: true }]}
          empty={<div className="font-medium text-body">No signed-in devices</div>}
          error={
            loadFailed && (
              <>
                <div className="font-medium text-body">Signed-in devices couldn&apos;t be loaded</div>
                <div className="mt-1">
                  <TextLink onClick={load}>Try again</TextLink>
                </div>
              </>
            )
          }
        />
      </SessionRows.Provider>
      {signingOut && (
        <ActionDialog
          title={`Sign out ${deviceName(signingOut)}?`}
          description="The device's session ends, and it will need to sign in again to use Morta."
          confirmLabel="Sign Out"
          danger
          onClose={() => setSigningOut(undefined)}
          onConfirm={() => signOut(signingOut)}
        />
      )}
    </SettingsCard>
  )
}

export default DevicesCard
